/**
 * Consent to sync. FR-502, L-04, and the reason sync is off until it exists.
 *
 * ## Why this is a module and not a checkbox
 *
 * Menstrual data is special-category under GDPR Article 9 and
 * *özel nitelikli kişisel veri* under KVKK Article 6. The workable basis for
 * processing it is **explicit consent** — GDPR 9(2)(a), KVKK *açık rıza* — and
 * "explicit" is a higher bar than ordinary consent: specific, informed,
 * unambiguous, freely given, separately obtained, not bundled, and as easy to
 * withdraw as to give.
 *
 * A pre-ticked box inside onboarding satisfies none of that. So consent is its
 * own deliberate step, it records what was agreed to and when, and withdrawing it
 * actually stops the network rather than setting a preference (FR-509).
 *
 * ## Why the version matters
 *
 * Consent is to a specific description of the processing. If that description
 * changes — a new relay jurisdiction, a change to what the relay retains — the
 * old consent no longer covers it, and continuing to sync on it would be
 * processing without a basis. So the version is stored, and a bump means asking
 * again rather than assuming.
 *
 * ## What is deliberately NOT recorded
 *
 * No IP address, no user agent, no identifier. A consent record is supposed to
 * evidence that the user agreed; building a small audit trail of who and from
 * where would create exactly the kind of data this project exists not to hold.
 */
import { openDb, promisify, STORE, txDone } from './db'

/**
 * Bump this when the description of the processing changes.
 *
 * The version is not decoration: a change means every existing consent stops
 * covering what the app now does, sync stops, and the user is asked again. That is
 * the intended cost of changing the terms.
 */
export const SYNC_CONSENT_VERSION = 1

const CONSENT_KEY = 'syncConsent'

export interface SyncConsent {
  version: number
  /** Epoch ms. Evidence of when, which is all that is needed. */
  grantedAt: number
  /**
   * The relay hosts the user was shown when they agreed.
   *
   * Consent is to specific recipients, not to "syncing" in the abstract. Storing
   * what was on screen is what makes it possible to notice later that the set has
   * changed — see `consentCoversRelays`.
   */
  relayHosts: string[]
}

export async function readSyncConsent(): Promise<SyncConsent | undefined> {
  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readonly')
  return promisify<SyncConsent | undefined>(tx.objectStore(STORE.meta).get(CONSENT_KEY))
}

/**
 * Record consent.
 *
 * Takes the relay hosts the user was actually shown, so the record describes what
 * they agreed to rather than what happens to be configured now.
 */
export async function grantSyncConsent(
  relayHosts: readonly string[],
  now: number = Date.now(),
): Promise<SyncConsent> {
  const consent: SyncConsent = {
    version: SYNC_CONSENT_VERSION,
    grantedAt: now,
    relayHosts: [...relayHosts].sort(),
  }
  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readwrite')
  tx.objectStore(STORE.meta).put(consent, CONSENT_KEY)
  await txDone(tx)
  return consent
}

/**
 * Withdraw consent.
 *
 * Deletes the record rather than flagging it withdrawn. There is no reason to
 * keep evidence of a consent that no longer applies, and "we kept a note that you
 * once agreed" is not something a user of this app should have to hear.
 *
 * The caller must also stop the transport (FR-509). Withdrawal that left a
 * connection open would make this a preference rather than a right.
 */
export async function withdrawSyncConsent(): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readwrite')
  tx.objectStore(STORE.meta).delete(CONSENT_KEY)
  await txDone(tx)
}

export type ConsentStatus =
  | { state: 'none' }
  | { state: 'outdated'; consent: SyncConsent }
  | { state: 'relays-changed'; consent: SyncConsent; added: string[] }
  | { state: 'valid'; consent: SyncConsent }

/**
 * Whether sync may run.
 *
 * Three ways consent can fail to cover what the app would do, and each one has to
 * stop sync rather than be treated as a warning:
 *
 *  - **none** — never given, or withdrawn.
 *  - **outdated** — the description of the processing has changed since.
 *  - **relays-changed** — a relay the user never saw has appeared in the list. A
 *    new recipient is new processing, and a signed list refresh must not be able
 *    to quietly widen who receives the data.
 */
export function consentStatus(
  consent: SyncConsent | undefined,
  currentRelayHosts: readonly string[],
): ConsentStatus {
  if (consent === undefined) return { state: 'none' }
  if (consent.version !== SYNC_CONSENT_VERSION) return { state: 'outdated', consent }

  const known = new Set(consent.relayHosts)
  const added = currentRelayHosts.filter((host) => !known.has(host))
  // Only additions matter. A relay disappearing narrows the recipient set, which
  // needs no fresh permission.
  if (added.length > 0) return { state: 'relays-changed', consent, added: added.sort() }

  return { state: 'valid', consent }
}

export const consentAllowsSync = (status: ConsentStatus): boolean => status.state === 'valid'
